import styled from '@emotion/styled'
import { styled as muiStyled } from '@mui/material/styles'
import Box from './Box'
import GrooverText from './GrooverText'
import { Activity, Client, EarlyBirdBooking } from '../../../pure-js/types/GrooverTypes'
import { getActiveDiscounts } from '../../../pure-js/libs/DiscountHelper'
import { State, EarlyBirdBookingData } from '../hooks/useAppState'
import iconCompleted from '/public/iconCompleted.svg'
import removeCourseIcon from '/public/removeCourseIcon.svg'
import addCourseIcon from '/public/addCourseIcon.svg'
import iconArrow from '/public/iconArrow.svg'
import { memo, useEffect, useMemo, useState, useRef } from 'react'
import { ClosedContainer, ContinueButton, OpenContainer } from './StyledComponents'
import { EIGHT_PX, FOUR_PX, SIX_PX, SIXTEEN_PX, TEN_PX, TWO_PX } from '../enums/Spacings'
import { Link, useSearchParams } from 'react-router-dom'
import { getClient, getClientActivities, useEarlyBirdBookingsForActivity } from '../libs/DBApiHandler'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import dayjs from 'dayjs'
import { BorderRadixes } from '../enums/BorderRadixes'
import { getWeekdayAbbreviation } from '../libs/DayJsHelper'
import FreeDropinRewardPreview from './FreeDropinRewardPreview'
import PriceContainer from './PriceContainer'
import useDiscountCalculations, { calculateDiscountData } from '../hooks/useDiscountCalculations'
import { getGrooverText, formatGrooverText } from '../libs/GrooverTextRepository'
import {
  getActivePeriodsForUser,
  getUserPreviousDiscounts,
  createEarlyBirdBooking
} from '../libs/CloudFunctionsApiHandler'
import { validateDiscountCode } from '../hooks/useDiscountCode'
import {
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputAdornment,
  InputBase,
  Tooltip
} from '@mui/material'
import { OpenInNew, Add, Close, Error, Block, CheckCircle, Search, CardGiftcard } from '@mui/icons-material'
import { getLocalizedActivityType, hasPricedSelectedActivity, isActivityFree } from '../utils/activityUtils'
import * as Yup from 'yup'
import { useForm } from '../hooks/useForm'
import { Controller } from 'react-hook-form'
import { TextFieldWithLabelForm } from './TextFieldWithLabelForm'
import useIsMobile from '../hooks/useIsMobile'
import ActivityStatusBadge from './ActivityStatusBadge'
import { DanceRoles } from '../../../pure-js/types/GrooverTypes'
import { ActivityRegistrationStatus } from '../../../pure-js/types/GrooverTypes'
import { getRoleRestrictionsForActivity } from '../helpers/RegistrationRoleRestrictionHelper'
import { isValidPersonName } from '../helpers/CourseRegistrationStepOneHelper'
import { getSelectableDanceRole, isRoleSelectionRequired } from '../helpers/RegistrationRoleHelper'
import {
  applyRegistrationStats,
  findSelectedPricingConfig,
  getBestAvailablePrice
} from '../../../pure-js/libs/calculateLogic/DiscountPricingHelpers'
import { CourseComboDiscount } from '../../../pure-js/types/DiscountTypes'
import { useCurrency } from '../hooks/useCurrency'
import { DiscountCode } from '../../../pure-js/types/DiscountCodeTypes'
import { GetUserPreviousDiscountsResponse } from '../../../pure-js/types/QueryParamTypes'
import { useFreeDropinRewardPreview } from '../hooks/useFreeDropinRewardPreview'
import {
  getRuleForActivity,
  isRuleActive,
  normalizeFreeDropinRule,
  ruleAllowsHelpDancers
} from '../../../pure-js/libs/calculateLogic/FreeDropinTicketLogic'
import type { FreeDropinPriorQualificationCount } from '../../../pure-js/types/QueryParamTypes'
import { FreeDropinPreviewUserRole, loadFreeDropinRewardPreview } from '../libs/FreeDropinRewardPreviewApi'

const emptyPreviousDiscounts: GetUserPreviousDiscountsResponse = {
  userEnrolledActivityIds: [],
  userEnrolledPeriodIds: [],
  userEnrolledActivityPeriods: [],
  usedContinuationDiscountIds: [],
  usedContinuationDiscountActivities: []
}

type Props = {
  state: State
  activity?: Activity
  onNextStep: () => void
  onOpenStepTwo: () => void
  registerActiveStep: number
  setFinalPrice: (price: string) => void
  onSetEnrollmentComment: (enrollmentComment: string) => void
  onSetEnrollmentDiscountCode: (code: DiscountCode | undefined) => void
  onSetEnrollmentRegisteredPartners: (partners: Record<string, string>) => void
  onSetSelectedActivities: (selectedActivities: Activity[]) => void
  onSetMainUserActivePeriods: (mainUserActivePeriods: string[]) => void
  onSetPartnerUserActivePeriods: (partnerUserActivePeriods: string[]) => void
  onSetMainUserPreviousDiscounts: (data: GetUserPreviousDiscountsResponse) => void
  onSetPartnerUserPreviousDiscounts: (data: GetUserPreviousDiscountsResponse) => void
  onSetRole: (role: DanceRoles | null) => void
  onSetEarlyBirdBooking: (earlyBirdBooking: Record<string, EarlyBirdBookingData>) => void
  onSetSingleEventQuantity?: (quantity: number) => void
  onSetChildrenInput?: (childrenInput: Record<string, { name: string; birthYear: string }>) => void
}

interface FormData {
  comment: string
  discountCode: string
  partners: Record<string, string>
  partnerCheckboxes: Record<string, boolean>
  singleEventQuantity: number
}

export const ValidationSchema = Yup.object().shape({
  comment: Yup.string().optional(),
  discountCode: Yup.string().optional(),
  partners: Yup.object().shape({}).nullable(),
  partnerCheckboxes: Yup.object().shape({}).nullable(),
  singleEventQuantity: Yup.number().min(1).max(10).required()
})

const BadgeLinkRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${SIX_PX};
  @media (max-width: 480px) {
    gap: 2px;
    flex-direction: column;
    align-items: flex-end;
  }
`

const ResponsiveActivityStatusBadge = styled(ActivityStatusBadge)`
  @media (max-width: 480px) {
    font-size: 0.6rem !important;
    font-weight: 500 !important;
    .MuiTypography-root,
    span,
    div {
      font-size: 0.6rem !important;
      font-weight: 500 !important;
    }
    padding: 2px 8px !important;
  }
`

function isActivityClosedOrFull(registrationStatus?: ActivityRegistrationStatus) {
  return (
    registrationStatus === ActivityRegistrationStatus.Closed || registrationStatus === ActivityRegistrationStatus.Full
  )
}

const CourseRegistrationStepTwo = ({
  registerActiveStep,
  onNextStep,
  onOpenStepTwo,
  activity,
  state,
  setFinalPrice,
  onSetEnrollmentComment,
  onSetEnrollmentDiscountCode,
  onSetEnrollmentRegisteredPartners,
  onSetSelectedActivities,
  onSetMainUserActivePeriods,
  onSetPartnerUserActivePeriods,
  onSetMainUserPreviousDiscounts,
  onSetPartnerUserPreviousDiscounts,
  onSetRole,
  onSetEarlyBirdBooking,
  onSetSingleEventQuantity,
  onSetChildrenInput
}: Props) => {
  const [searchParams] = useSearchParams()
  const langQuery = searchParams.get('lang')

  const [allActivities, setAllActivities] = useState<Activity[] | null>(null)
  const [client, setClient] = useState<Client | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [showAddSection, setShowAddSection] = useState(false)
  const [activeAddTab, setActiveAddTab] = useState(0)
  const [showPastActivities, setShowPastActivities] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCommentInput, setShowCommentInput] = useState(false)
  const [childValidationErrors, setChildValidationErrors] = useState<
    Record<string, { name?: boolean; birthYear?: boolean }>
  >({})
  const [showDiscountCodeInput, setShowDiscountCodeInput] = useState(false)
  const [error, setError] = useState(false)
  const [roleValidationError, setRoleValidationError] = useState(false)
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const [activityWithStats, setActivityWithStats] = useState<Activity | undefined>(activity)
  const earlyBirdBookings = useEarlyBirdBookingsForActivity(activity)
  const hasChildActivity = state.selectedActivities.some((a) => a.allowChildRegistration)
  const registrationTypeCouple = state.individualFlow && !hasChildActivity
  const registrationOverrideMode = activity?.registrationOverride?.mode
  const registrationOverrideShowRoleWithValidation =
    activity?.registrationOverride?.individual?.showRoleWithValidation ?? true
  const registrationOverrideAllowPartnerRegistration =
    activity?.registrationOverride?.individual?.allowPartnerRegistration ?? true
  const hasRegistrationOverride = !!registrationOverrideMode
  const disableAdditionalActivities = hasRegistrationOverride || activity?.disableAdditionalActivities === true
  const allowParentEventSelectionWhenDisableAdditional =
    activity?.eventType === 'Parentevent' && !hasRegistrationOverride && activity?.disableAdditionalActivities === true
  const canRegisterPartnersForActivities = !(
    registrationOverrideMode === 'individual' && registrationOverrideAllowPartnerRegistration === false
  )
  const shouldShowRoleSelection = isRoleSelectionRequired(state, activity)
  const hasPricedSelection = hasPricedSelectedActivity(activity, state.selectedActivities)
  // Step 3 validates the reducer state, so Step 2 must render the same role source.
  // The hook's local role can otherwise make this radio look selected too early.
  const resolvedRole = getSelectableDanceRole(state.obfuscatedEnrollment?.mainUser?.type || state.role)
  const isRoleMissing =
    shouldShowRoleSelection && !resolvedRole && !state.isRegistrationCompleted && !state.isRegistrationSubmitted
  const earlyBirdBookingAttemptedRef = useRef<boolean>(false)
  const preloadedDiscountAppliedRef = useRef<boolean>(false)
  const currency = useCurrency(client)
  const [discountCodeError, setDiscountCodeError] = useState<string>('')
  const [isVerifyingDiscountCode, setIsVerifyingDiscountCode] = useState<boolean>(false)
  const [isDiscountCodeValid, setIsDiscountCodeValid] = useState<boolean>(
    state.discountCode !== undefined && state.discountCode.code.length > 0
  )

  const formProps = useForm<FormData>(ValidationSchema, {
    defaultValues: {
      comment: '',
      discountCode: state.discountCode?.code || '',
      partners: {},
      partnerCheckboxes: {},
      singleEventQuantity: state?.singleEventQuantity || 1
    },
    mode: 'all',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (disableAdditionalActivities && showAddSection) {
      setShowAddSection(false)
    }
  }, [disableAdditionalActivities, showAddSection])

  useEffect(() => {
    const shouldForceSingleRole =
      registrationOverrideMode === 'individual' && registrationOverrideShowRoleWithValidation === false
    if (!shouldForceSingleRole) return
    if (state.obfuscatedEnrollment?.mainUser?.type) return
    if (state.role === DanceRoles.Single) return
    onSetRole(DanceRoles.Single)
  }, [
    registrationOverrideMode,
    registrationOverrideShowRoleWithValidation,
    state.obfuscatedEnrollment?.mainUser?.type,
    state.role,
    onSetRole
  ])

  useEffect(() => {
    if (canRegisterPartnersForActivities) return
    // Clear any previously selected partner-registration data to avoid sending stale values.
    if (Object.keys(state.registeredPartners || {}).length > 0) {
      onSetEnrollmentRegisteredPartners({})
    }
    formProps.setValue('partnerCheckboxes', {})
    formProps.setValue('partners', {})
  }, [canRegisterPartnersForActivities])

  const handleNext = async () => {
    setRoleValidationError(false)

    const resolvedRole = getSelectableDanceRole(state.obfuscatedEnrollment?.mainUser?.type || state.role)
    if (shouldShowRoleSelection && !resolvedRole) {
      setRoleValidationError(true)
      return
    }

    // Validate child name + birth year for child-enabled activities
    if (hasChildActivity && onSetChildrenInput) {
      const currentYear = new Date().getFullYear()
      const errors: Record<string, { name?: boolean; birthYear?: boolean }> = {}
      state.selectedActivities
        .filter((a) => a.allowChildRegistration)
        .forEach((a) => {
          const input = state.childrenInput[a.id]
          const nameError = !input?.name?.trim()
          const rawYear = input?.birthYear?.trim() || ''
          const parsedYear = parseInt(rawYear, 10)
          const birthYearError = !rawYear || !/^\d{4}$/.test(rawYear) || parsedYear < 1900 || parsedYear > currentYear
          if (nameError || birthYearError) {
            errors[a.id] = { name: nameError, birthYear: birthYearError }
          }
        })
      if (Object.keys(errors).length > 0) {
        setChildValidationErrors(errors)
        return
      }
      setChildValidationErrors({})
    }

    const fieldsToValidate =
      showCommentInput && !showDiscountCodeInput
        ? (['comment', 'partners'] as const)
        : showCommentInput && showDiscountCodeInput
          ? (['comment', 'partners'] as const)
          : !showCommentInput && showDiscountCodeInput
            ? (['partners'] as const)
            : (['partners'] as const)
    // Trigger form validation
    const isValid = await formProps.trigger(fieldsToValidate)

    const formData = formProps.getValues()

    // Additional validation for partner names when checkboxes are checked
    const checkedPartners = Object.entries(formData.partnerCheckboxes || {})
      .filter(([, isChecked]) => isChecked)
      .map(([activityId]) => activityId)

    // Clear manual errors from previous submit attempts so only currently invalid names show errors.
    // The Yup schema doesn't cover partners.*, so RHF never clears these on its own.
    checkedPartners.forEach((activityId) => formProps.clearErrors(`partners.${activityId}`))

    const missingPartnerNames = checkedPartners.filter((activityId) => !formData.partners?.[activityId]?.trim())
    const invalidPartnerNames = checkedPartners.filter(
      (activityId) =>
        !missingPartnerNames.includes(activityId) && !isValidPersonName(formData.partners?.[activityId] ?? '')
    )

    if (missingPartnerNames.length > 0 || invalidPartnerNames.length > 0) {
      missingPartnerNames.forEach((activityId) => {
        formProps.setError(`partners.${activityId}`, {
          type: 'required',
          message: getGrooverText('registrationValidationsErrorPartnerNameRequired')
        })
      })
      invalidPartnerNames.forEach((activityId) => {
        formProps.setError(`partners.${activityId}`, {
          type: 'validate',
          message: getGrooverText('registrationValidationsErrorPartnerNameInvalid')
        })
      })
      return
    }

    if (!isValid) {
      // Form has validation errors, don't proceed
      return
    }

    onSetEnrollmentComment(formData.comment)
    onSetEnrollmentRegisteredPartners(formData.partners)
    onNextStep()
  }

  const handleVerifyDiscountCode = async () => {
    const code = formProps.getValues('discountCode')
    if (!code || !client?.id) return

    setIsVerifyingDiscountCode(true)
    setDiscountCodeError('')

    const result = await validateDiscountCode(client.id, code)

    if (result.isValid) {
      // Valid discount code
      setDiscountCodeError('')
      setIsDiscountCodeValid(true)
      onSetEnrollmentDiscountCode(result.data!)
      // Clear any form errors for discount code
      formProps.clearErrors('discountCode')
    } else {
      // Invalid discount code
      const errorMessage = result.error === 'fetch-error' ? 'discount-error' : result.error
      setDiscountCodeError(errorMessage)
      setIsDiscountCodeValid(false)
      onSetEnrollmentDiscountCode(undefined)
      formProps.setError('discountCode', {
        type: 'manual',
        message: getGrooverText('registrationValidationsErrorDiscountCodeInvalid')
      })
    }

    setIsVerifyingDiscountCode(false)
  }

  const handleClearDiscountCode = () => {
    formProps.setValue('discountCode', '')
    setDiscountCodeError('')
    setIsDiscountCodeValid(false)
    onSetEnrollmentDiscountCode(undefined)
    formProps.clearErrors('discountCode')
  }

  const handleCloseDiscountCodeInput = () => {
    setShowDiscountCodeInput(false)
    handleClearDiscountCode()
  }

  useEffect(() => {
    if (preloadedDiscountAppliedRef.current) return
    const urlDiscountCode = searchParams.get('discountCode')?.trim()
    if (!urlDiscountCode) return
    if (!client) return
    if (state.discountCode?.code) return
    if (state.isRegistrationSubmitted) return
    const hasCodeDiscount = client.discounts?.some((d) => d.type === 'code')
    if (!hasCodeDiscount) return

    preloadedDiscountAppliedRef.current = true
    setShowDiscountCodeInput(true)
    formProps.setValue('discountCode', urlDiscountCode)
    handleVerifyDiscountCode()
  }, [client])

  const handleAddTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveAddTab(newValue)
  }

  // Helper functions for membership activity detection
  const isMembershipActivity = (activity: Activity): boolean => {
    return client?.membershipConfig?.specificActivityIds?.includes(activity.id) || false
  }

  const getMembershipPeriodForActivity = (activity: Activity): string => {
    const timeframe = client?.membershipConfig?.timeframe || 'same_period'
    // Use periodId if available, fallback to legacy period field (|| intentional: treats empty string as missing)
    const activityPeriod = activity.periodId || activity.period || ''

    if (timeframe === 'same_year') {
      // Resolve year via Period.periodStart when periodId is set (opaque ID has no year in it)
      if (activity.periodId && client?.periods) {
        const period = client.periods.find((p) => p.id === activity.periodId)
        if (period?.periodStart) return new Date(period.periodStart).getFullYear().toString()
      }
      // Legacy fallback: extract 4-digit year from strings like "HT 2024"
      const match = activityPeriod.match(/\d{4}/)
      return match ? match[0] : activityPeriod.substring(0, 4)
    } else if (timeframe === 'anytime') {
      return 'ANYTIME_MEMBERSHIP'
    }
    return activityPeriod
  }

  const hasPreviousMembershipForPeriod = (membershipPeriod: string): boolean => {
    // Check if this period exists in user's periods but no cart activities currently grant it
    const currentCartMembershipPeriods = state.selectedActivities
      .filter((a) => isMembershipActivity(a))
      .map((a) => getMembershipPeriodForActivity(a))

    return (
      (state.mainUserActivePeriods || []).includes(membershipPeriod) &&
      !currentCartMembershipPeriods.includes(membershipPeriod)
    )
  }

  // Enhanced activity handlers
  const handleAddActivity = (activityToAdd: Activity) => {
    if (disableAdditionalActivities) {
      // If additional activities are disabled, only allow adding:
      // - the root activity itself, or
      // - (special case) subevents belonging to the parent event (when explicitly allowed)
      const rootActivityId = activity?.id
      if (!rootActivityId) return

      if (allowParentEventSelectionWhenDisableAdditional) {
        const isSubeventForParent = activityToAdd.subeventParentId === rootActivityId
        if (!isSubeventForParent) return
      } else {
        if (activityToAdd.id !== rootActivityId) return
      }
    }

    // Clear package selection when manually adding activities
    if (selectedPackageId) {
      setSelectedPackageId(null)
    }
    const newSelectedActivities = [...state?.selectedActivities, activityToAdd]
    onSetSelectedActivities(newSelectedActivities)

    // Check if we added a membership activity
    if (isMembershipActivity(activityToAdd)) {
      const membershipPeriod = getMembershipPeriodForActivity(activityToAdd)
      const currentPeriods = state.mainUserActivePeriods || []

      // Only add if not already present (handles previous membership)
      if (!currentPeriods.includes(membershipPeriod)) {
        onSetMainUserActivePeriods([...currentPeriods, membershipPeriod])
      }
    }
  }

  const handleRemoveActivity = (activityToRemove: Activity) => {
    if (disableAdditionalActivities && !allowParentEventSelectionWhenDisableAdditional) {
      return
    }

    // Clear package selection when manually removing activities
    if (selectedPackageId) {
      setSelectedPackageId(null)
    }
    const newSelectedActivities = state?.selectedActivities.filter((a) => a.id !== activityToRemove.id)
    onSetSelectedActivities(newSelectedActivities)

    // Check if we removed a membership activity
    if (isMembershipActivity(activityToRemove)) {
      const membershipPeriod = getMembershipPeriodForActivity(activityToRemove)

      // Check if user had this period from PREVIOUS registrations
      const hadPreviousMembership = hasPreviousMembershipForPeriod(membershipPeriod)

      // Check if there are remaining membership activities in current cart for this period
      const remainingMembershipActivities = newSelectedActivities.filter(
        (a) => isMembershipActivity(a) && getMembershipPeriodForActivity(a) === membershipPeriod
      )

      // Only remove the period if:
      // 1. No remaining membership activities for this period in cart AND
      // 2. User didn't have this membership from previous registrations
      if (remainingMembershipActivities.length === 0 && !hadPreviousMembership) {
        const updatedPeriods = (state.mainUserActivePeriods || []).filter((p) => p !== membershipPeriod)
        onSetMainUserActivePeriods(updatedPeriods)
      }
    }
  }

  // Extract packages from combo discounts for the parent event
  const getPackagesForParentEvent = (
    client: Client | undefined,
    parentEventId: string | undefined,
    allActivities: Activity[] | null
  ): Array<CourseComboDiscount & { calculatedPrice: number }> => {
    if (!client?.discounts || !parentEventId || !allActivities) {
      return []
    }

    // Find combo discounts (filter out deleted ones)
    const activeDiscounts = getActiveDiscounts(client.discounts)
    const comboDiscounts = activeDiscounts.filter((discount) => discount.type === 'combo') as Array<
      import('../../../pure-js/types/DiscountTypes').DiscountComboDiscount
    >

    // Extract all packages from combo discounts
    const allPackages: CourseComboDiscount[] = []
    comboDiscounts.forEach((discount) => {
      if (discount.coursesDiscounts) {
        allPackages.push(...discount.coursesDiscounts)
      }
    })

    // Filter packages for this parent event
    const packagesForParent = allPackages.filter((pkg) => pkg.parentEventId === parentEventId)

    // Calculate price for each package and return unique packages by name
    const packageMap = new Map<string, CourseComboDiscount & { calculatedPrice: number }>()

    packagesForParent.forEach((pkg) => {
      // Calculate package price: sum of subevent prices minus discount
      const packageSubevents = allActivities.filter((subevent) => pkg.courses?.includes(subevent.id))
      const totalPrice = packageSubevents.reduce((sum, subevent) => sum + (subevent.price || 0), 0)
      const calculatedPrice = Math.max(0, totalPrice - (pkg.discount || 0))

      // Use package name as key, or generate one if missing
      const packageName = pkg.name || `Package (${pkg.courses?.length || 0} subevents)`

      // Only add if we haven't seen this package name, or if this one has a better price
      if (!packageMap.has(packageName) || packageMap.get(packageName)!.calculatedPrice > calculatedPrice) {
        packageMap.set(packageName, {
          ...pkg,
          calculatedPrice
        })
      }
    })

    return Array.from(packageMap.values())
  }

  // Handle package selection - clears preselected activities and selects package subevents
  // Only one package can be selected at a time (mutually exclusive)
  const handlePackageSelect = (pkg: CourseComboDiscount & { calculatedPrice: number }) => {
    if (!allActivities || !pkg.courses) {
      return
    }

    // Create a unique identifier for this package
    const packageId = pkg.name || `package-${pkg.courses?.sort().join('-')}`

    // If this package is already selected, deselect it (toggle behavior)
    if (selectedPackageId === packageId) {
      setSelectedPackageId(null)
      onSetSelectedActivities([])
      return
    }

    // Select this package (mutually exclusive - only one package at a time)
    setSelectedPackageId(packageId)

    // Clear all currently selected activities
    onSetSelectedActivities([])

    // Find subevents by IDs from package.courses array
    const packageSubevents = allActivities.filter((subevent) => {
      // Check if subevent is in package courses
      const isInPackage = pkg.courses?.includes(subevent.id) || false

      // Only include subevents that are not disabled (not Closed/Full)
      const isNotDisabled =
        subevent.registrationStatus !== ActivityRegistrationStatus.Closed &&
        subevent.registrationStatus !== ActivityRegistrationStatus.Full

      return isInPackage && isNotDisabled
    })

    // Add all package subevents
    onSetSelectedActivities(packageSubevents)
  }

  // Group activities by day
  const groupActivitiesByDay = (activities: Activity[]) => {
    const grouped = activities.reduce(
      (acc, activity) => {
        const date = dayjs(activity.startDate).locale('sv')
        const dayKey = date.format('YYYY-MM-DD')
        const dayName = date.format('dddd') // Full day name in Swedish
        const dayShort = date.format('ddd') // Short day name in Swedish
        const dateFormatted = date.format('D/M')

        if (!acc[dayKey]) {
          acc[dayKey] = {
            date: dayKey,
            dateFormatted,
            dayName,
            dayShort,
            activities: []
          }
        }
        acc[dayKey].activities.push(activity)
        return acc
      },
      {} as Record<
        string,
        { date: string; dateFormatted: string; dayName: string; dayShort: string; activities: Activity[] }
      >
    )

    // Sort by date and activities by time
    return Object.values(grouped)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((group) => ({
        ...group,
        activities: group.activities.sort((a, b) => {
          const timeA = (a.time || '00:00').split(':').map(Number)
          const timeB = (b.time || '00:00').split(':').map(Number)
          return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1])
        })
      }))
  }

  // Get day color based on day index
  const getDayColor = (index: number): string => {
    const colors = ['#7c3aed', '#2563eb', '#059669', '#dc2626', '#ea580c', '#ca8a04', '#9333ea']
    return colors[index % colors.length]
  }

  const filterActivitiesByType = (
    activities: Activity[] | null,
    type: 'Course' | 'Occasion' | 'Related' | 'Other',
    includePast?: boolean
  ) => {
    const threeWeeksAgo = dayjs().subtract(15, 'days').startOf('day')

    const filteredActivities = (activities || [])
      .filter((activity) => {
        // Check if activity has already started (skip date check for pinned activities or when includePast is true)
        if (!activity.pinToTopOfRegistrationList && !includePast) {
          const activityDate = dayjs(activity.startDate).startOf('day')
          if (activityDate.isBefore(threeWeeksAgo)) return false
        }

        // Exclude activities with "special" registration status
        if (activity.registrationStatus === ActivityRegistrationStatus.Special) return false

        // First check if the activity is already selected
        const isSelected = state?.selectedActivities?.some((a) => a.id === activity?.id)
        if (isSelected) return false

        // Then check if there's any selected activity with the same start date and time
        const hasConflict = state?.selectedActivities?.some(
          (selectedActivity) =>
            dayjs(selectedActivity.startDate).format('YYYY-MM-DD') === dayjs(activity.startDate).format('YYYY-MM-DD') &&
            selectedActivity.time === activity.time
        )
        if (hasConflict) return false

        // For Related type, check if activity's groupingCode matches any of the selected activities' groupingCodes
        if (type === 'Related') {
          const selectedGroupingCodes = state?.selectedActivities?.map((a) => a.groupingCode).filter((code) => code)
          return activity.groupingCode && selectedGroupingCodes?.includes(activity.groupingCode)
        }

        return activity.type === type
      })
      .sort((a, b) => new Date(a.startDate || '').getTime() - new Date(b.startDate || '').getTime())

    // Group activities by groupingCode
    return filteredActivities.reduce((groups: Record<string, Activity[]>, activity) => {
      const key = activity.groupingCode || activity.id
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(activity)
      return groups
    }, {})
  }

  const countHiddenPastActivities = (activities: Activity[] | null): number => {
    const threeWeeksAgo = dayjs().subtract(15, 'days').startOf('day')
    return (activities || []).filter((activity) => {
      if (activity.pinToTopOfRegistrationList) return false
      const activityDate = dayjs(activity.startDate).startOf('day')
      if (!activityDate.isBefore(threeWeeksAgo)) return false
      if (activity.registrationStatus === ActivityRegistrationStatus.Special) return false
      if (state?.selectedActivities?.some((a) => a.id === activity.id)) return false
      const hasConflict = state?.selectedActivities?.some(
        (selected) =>
          dayjs(selected.startDate).format('YYYY-MM-DD') === dayjs(activity.startDate).format('YYYY-MM-DD') &&
          selected.time === activity.time
      )
      if (hasConflict) return false
      return activity.type === 'Course' || activity.type === 'Occasion' || activity.type === 'Other'
    }).length
  }

  const formatMonthHeader = (dateKey: string) => {
    const date = dayjs(dateKey + '-01') // Add day to ensure valid date
    return date.toDate().toLocaleString('sv-SE', { month: 'short', year: 'numeric' })
  }

  const groupActivitiesByMonth = (activities: Activity[]) => {
    // Normalize all dates to the first day of their month to ensure proper grouping
    return activities.reduce((groups: { [key: string]: Activity[] }, activity) => {
      const date = dayjs(activity.startDate).startOf('month')
      const key = date.format('YYYY-MM') // Use consistent date format
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(activity)
      return groups
    }, {})
  }

  // This function is used to render events that grouped per month
  const renderGroupedActivities = (groupedActivities: Record<string, Activity[]>) => {
    // Always flatten activities and group by month
    const allActivitiesFlat = Object.values(groupedActivities).flat()
    const byMonth = groupActivitiesByMonth(allActivitiesFlat)

    return Object.entries(byMonth).map(([monthKey, monthActivities]) => (
      <Box key={monthKey} fullWidth>
        <MonthHeader>{formatMonthHeader(monthKey)}</MonthHeader>
        {monthActivities.map((activity) => (
          <ActivityListItem
            id="activity-list-item-container"
            key={activity?.id}
            fullWidth
            direction="row"
            align="flex-start"
            justify="flex-start"
            left
            spacing={EIGHT_PX}
            gap={EIGHT_PX}
            onClick={() => {
              if (!isActivityClosedOrFull(activity.registrationStatus)) {
                handleAddActivity(activity)
              }
            }}
            style={{
              cursor: isActivityClosedOrFull(activity.registrationStatus) ? 'not-allowed' : 'pointer',
              opacity: isActivityClosedOrFull(activity.registrationStatus) ? 0.5 : 1
            }}
          >
            <Box id="activivy-list-item-selector" direction="row" align="center" gap={TWO_PX} top spacing={EIGHT_PX}>
              {isActivityClosedOrFull(activity.registrationStatus) ? (
                <Block sx={{ fontSize: 16, width: 16, height: 16, color: '#e57373' }} />
              ) : (
                <img src={addCourseIcon} width={16} height={16} />
              )}
            </Box>
            <Box id="activity-list-main-container" fullWidth flexGrow={1} style={{ minWidth: 0 }}>
              <ActivityItemTitle id="activity-list-item-title" fullWidth>
                <span>{activity?.name}</span>
              </ActivityItemTitle>
              <Box
                id="activity-list-item-metadata"
                fullWidth
                direction="row"
                align="center"
                justify="space-between"
                gap={FOUR_PX}
              >
                <Box direction="row" align="center" gap={FOUR_PX} flexGrow={1}>
                  <StartDate fullPadding spacing={FOUR_PX}>
                    <span>{dayjs(activity?.startDate).format('D/M')}</span>
                  </StartDate>
                  <Box id="activity-list-item-weekday" direction="row" gap={FOUR_PX}>
                    <span>{getWeekdayAbbreviation(activity?.startDate)}</span>
                    <span>{activity?.time}</span>
                  </Box>
                </Box>
                <Box direction="row" align="center" gap="8px" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                  {renderFreeDropinQualifiesIcon(activity)}
                  {isActivityClosedOrFull(activity.registrationStatus) && (
                    <ResponsiveActivityStatusBadge registrationStatus={activity.registrationStatus} />
                  )}
                  <Link
                    style={{ paddingTop: FOUR_PX, display: 'inline-flex' }}
                    to={
                      client?.slug && activity?.seoLink
                        ? buildRoute(
                            RoutePath.ACTIVITY_DETAIL_SEO,
                            { clientSlug: client.slug, activitySlug: activity.seoLink },
                            langQuery ? { lang: langQuery } : undefined
                          )
                        : buildRoute(
                            RoutePath.ACTIVITY_DETAIL,
                            { activityId: activity?.id || '' },
                            langQuery ? { lang: langQuery } : undefined
                          )
                    }
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <OpenInNew sx={{ width: 20, height: 20, color: '#DFDFDF' }} />
                  </Link>
                </Box>
              </Box>
            </Box>
          </ActivityListItem>
        ))}
      </Box>
    ))
  }

  // Add this type definition before the component
  type TabItem = {
    label: string
    content: Record<string, Activity[]>
  }

  // Replace the getAvailableTabsAndContent function
  const getAvailableTabsAndContent = (allActivities: Activity[] | null) => {
    // Use all activities without singleRegistration filtering
    let filteredAllActivities = allActivities

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      filteredAllActivities = (filteredAllActivities || []).filter((a) => a.name?.toLowerCase().includes(q))
    }

    const showRelatedTab = activity?.groupingCode && state?.selectedActivities?.length > 0

    const relatedActivities = filterActivitiesByType(filteredAllActivities, 'Related', showPastActivities)
    const filteredCourses = filterActivitiesByType(filteredAllActivities, 'Course', showPastActivities)
    const filteredEvents = filterActivitiesByType(filteredAllActivities, 'Occasion', showPastActivities)
    const filteredOthers = filterActivitiesByType(filteredAllActivities, 'Other', showPastActivities)

    const hiddenCount = showPastActivities ? 0 : countHiddenPastActivities(filteredAllActivities)

    const showRelatedActivities = relatedActivities && Object.keys(relatedActivities).length > 0
    const showFilteredCourses = filteredCourses && Object.keys(filteredCourses).length > 0
    const showFilteredEvents = filteredEvents && Object.keys(filteredEvents).length > 0
    const showFilteredOthers = filteredOthers && Object.keys(filteredOthers).length > 0

    // Create array of available tabs with proper typing
    const availableTabs: TabItem[] = [
      showRelatedTab && showRelatedActivities
        ? {
            label: getGrooverText('externalActivityPageSegmentOtherTabRelated'),
            content: relatedActivities
          }
        : null,
      showFilteredCourses
        ? {
            label: getLocalizedActivityType('Course', client ?? ({} as Client)),
            content: filteredCourses
          }
        : null,
      showFilteredEvents
        ? {
            label: getLocalizedActivityType('Occasion', client ?? ({} as Client)),
            content: filteredEvents
          }
        : null,
      showFilteredOthers
        ? {
            label: getLocalizedActivityType('Other', client ?? ({} as Client)),
            content: filteredOthers
          }
        : null
    ].filter((tab): tab is TabItem => tab !== null)

    return (
      <>
        <AddActivitiesHeader fullWidth direction="row" align="center" justify="space-between">
          <GrooverText textKey="registrationStep3ActivitiesHeaderStep3AddMoreCourses" />
          <CloseButton
            onClick={() => {
              setShowAddSection(false)
              setShowPastActivities(false)
              setSearchQuery('')
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </CloseButton>
        </AddActivitiesHeader>

        <SearchInputWrapper>
          <InputBase
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={getGrooverText('registrationStep3SearchActivitiesPlaceholder')}
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#aaa' }} />
              </InputAdornment>
            }
            endAdornment={
              searchQuery ? (
                <InputAdornment position="end">
                  <Close sx={{ fontSize: 16, color: '#aaa', cursor: 'pointer' }} onClick={() => setSearchQuery('')} />
                </InputAdornment>
              ) : null
            }
          />
        </SearchInputWrapper>

        {(availableTabs.length > 0 || hiddenCount > 0 || showPastActivities) && (
          <>
            {availableTabs.length > 1 && (
              <TabsContainer>
                <Tabs value={activeAddTab} onChange={handleAddTabChange} variant="fullWidth">
                  {availableTabs.map((tab, index) => (
                    <Tab
                      key={index}
                      label={tab.label}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    />
                  ))}
                </Tabs>
              </TabsContainer>
            )}
            {availableTabs.length > 0 && (
              <TabContent fullWidth>
                {availableTabs[activeAddTab] && renderGroupedActivities(availableTabs[activeAddTab].content)}
              </TabContent>
            )}
            {(hiddenCount > 0 || showPastActivities) && (
              <ShowPastLink onClick={() => setShowPastActivities((prev) => !prev)}>
                {showPastActivities
                  ? getGrooverText('registrationStep3HidePastActivities')
                  : formatGrooverText(getGrooverText('registrationStep3ShowPastActivities'), [String(hiddenCount)])}
              </ShowPastLink>
            )}
          </>
        )}
      </>
    )
  }

  useEffect(() => {
    let isMounted = true

    const fetchActivity = async () => {
      try {
        let fetchedActivities = await getClientActivities(activity?.clientId)

        if (!isMounted) return

        if (activity?.eventType === 'Parentevent') {
          // select all activities with the same subeventParentId
          fetchedActivities = fetchedActivities
            .filter((a) => a.subeventParentId === activity?.id && !a.dropinOnly)
            .sort((a, b) => {
              // First, compare by startDate
              const dateA = new Date(a.startDate || '').getTime()
              const dateB = new Date(b.startDate || '').getTime()

              if (dateA !== dateB) {
                return dateA - dateB
              }

              // If startDates are the same, compare by time
              const timeA = (a.time || '00:00').split(':').map(Number)
              const timeB = (b.time || '00:00').split(':').map(Number)

              // Convert hours & minutes to total minutes and compare
              return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1])
            })
        } else {
          // Filter out activities with startDate more than 7 days ago, activities that have already been selected,
          // and activities from different cities
          fetchedActivities = fetchedActivities.filter((fetchedActivity) => {
            const isNotSelected = !state?.selectedActivities?.some((selected) => selected.id === fetchedActivity.id)
            // undefined city means everywhere - only filter if both cities are defined and different
            const isSameCity =
              !fetchedActivity.city || !activity?.city || fetchedActivity.city.trim() === activity?.city.trim()
            return isNotSelected && isSameCity && !fetchedActivity.dropinOnly
          })
        }

        // Pin-to-top: show activities with pinToTopOfRegistrationList first, then existing order (date/time)
        fetchedActivities = [...fetchedActivities].sort((a, b) => {
          const aPin = a.pinToTopOfRegistrationList ? 1 : 0
          const bPin = b.pinToTopOfRegistrationList ? 1 : 0
          if (bPin !== aPin) return bPin - aPin // pinned first
          const dateA = new Date(a.startDate || '').getTime()
          const dateB = new Date(b.startDate || '').getTime()
          if (dateA !== dateB) return dateA - dateB
          const timeA = (a.time || '00:00').split(':').map(Number)
          const timeB = (b.time || '00:00').split(':').map(Number)
          return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1])
        })

        if (isMounted) {
          setAllActivities(fetchedActivities)
        }
      } catch (err) {
        if (isMounted) {
          console.error(err)
        }
      }
    }

    const fetchClient = async () => {
      if (activity?.clientId) {
        const clientData = await getClient(activity.clientId)
        if (isMounted && clientData) {
          setClient(clientData)
        }
      }
    }

    if (activity?.id) {
      fetchActivity()
      fetchClient()
    }

    return () => {
      isMounted = false
    }
  }, [activity?.clientId])

  useEffect(() => {
    // if activity is not a parent event, set it as selected only if no activities are currently selected
    if (
      activity?.id &&
      activity.eventType !== 'Parentevent' &&
      (!state?.selectedActivities || state.selectedActivities.length === 0)
    ) {
      // Use handleAddActivity to ensure membership periods are properly handled
      handleAddActivity(activity)
    }
  }, [activity?.id, state?.selectedActivities])

  useEffect(() => {
    if (!activity?.id) return
    if (!disableAdditionalActivities) return
    if (allowParentEventSelectionWhenDisableAdditional) return
    if (!state?.selectedActivities) return

    // When additional activities are disabled (or an override is active), enforce a single-activity selection.
    if (state.selectedActivities.length !== 1 || state.selectedActivities[0]?.id !== activity.id) {
      onSetSelectedActivities([activity])
    }
  }, [
    activity?.id,
    disableAdditionalActivities,
    allowParentEventSelectionWhenDisableAdditional,
    state?.selectedActivities
  ])

  // Ensure membership periods are tracked for all selected activities
  useEffect(() => {
    if (state?.selectedActivities && state.selectedActivities.length > 0) {
      const membershipActivities = state.selectedActivities.filter((activity) => isMembershipActivity(activity))

      if (membershipActivities.length > 0) {
        const newPeriods = membershipActivities.map((activity) => getMembershipPeriodForActivity(activity))
        const currentPeriods = state.mainUserActivePeriods || []
        const periodsToAdd = newPeriods.filter((period) => !currentPeriods.includes(period))

        if (periodsToAdd.length > 0) {
          onSetMainUserActivePeriods([...currentPeriods, ...periodsToAdd])
        }
      }
    }
  }, [state?.selectedActivities])

  // Remove disabled subevents from selection when they become unavailable
  useEffect(() => {
    if (activity?.eventType === 'Parentevent' && allActivities && state?.selectedActivities) {
      const disabledSubevents = state.selectedActivities.filter((selected) =>
        allActivities.some(
          (subevent) =>
            subevent.id === selected.id &&
            (subevent.registrationStatus === ActivityRegistrationStatus.Closed ||
              subevent.registrationStatus === ActivityRegistrationStatus.Full)
        )
      )

      if (disabledSubevents.length > 0) {
        const updatedSelection = state.selectedActivities.filter(
          (selected) => !disabledSubevents.some((disabled) => disabled.id === selected.id)
        )
        onSetSelectedActivities(updatedSelection)
      }
    }
  }, [allActivities, activity?.eventType, state?.selectedActivities, onSetSelectedActivities])

  // Create early bird booking for Event type activities
  useEffect(() => {
    // Only create early bird booking once and only for Event type activities
    // Skip free courses (price = 0) as they don't need early bird pricing
    if (
      activity?.type === 'Event' &&
      activity?.price > 0 &&
      activity?.id &&
      state.name &&
      state.email &&
      state.phoneNumber &&
      (state.obfuscatedEnrollment?.mainUser?.type || state.role) &&
      !state.earlyBirdBooking[activity.id]?.bookingId &&
      !earlyBirdBookingAttemptedRef.current
    ) {
      const createBooking = async () => {
        try {
          const subeventType = state.discountType || 'single'
          const region = state.discountRegion
          console.log('Looking for early bird price for params', { subeventType, region })
          console.log('Activity discount config:', activity.discountConfig)
          // check and get early bird price
          const price = getBestAvailablePrice(activity, subeventType, region)
          if (!price) {
            console.log('No early bird price found for activity:', activity.id)
            earlyBirdBookingAttemptedRef.current = true
            return
          }
          console.log('early bird booking bestPrice', price)
          const pricingConfig = findSelectedPricingConfig(activity, subeventType, price, region)
          if (!pricingConfig) {
            console.log('No pricing config found for activity', activity.id, 'for early bird price', price)
            console.log('Available pricing configs:', activity.discountConfig?.pricingConfigs)
            earlyBirdBookingAttemptedRef.current = true
            return
          }
          const booking = await createEarlyBirdBooking({
            activityId: activity.id,
            price,
            pricingConfigId: pricingConfig.id,
            enrollmentUser: {
              name: state.name,
              email: state.email,
              phone: state.phoneNumber,
              type: (state.obfuscatedEnrollment?.mainUser?.type || state.role) as DanceRoles
            },
            registrationType: subeventType,
            region
          })
          // Update global state with the booking ID and price
          const newEarlyBirdBooking = { ...state.earlyBirdBooking }
          if (activity.id && booking.id) {
            newEarlyBirdBooking[activity.id] = {
              price,
              bookingId: booking.id,
              pricingConfigId: pricingConfig.id
            }
            onSetEarlyBirdBooking(newEarlyBirdBooking)
          }
          earlyBirdBookingAttemptedRef.current = true
          console.log('Early bird booking created successfully for activity:', activity.id, booking.id)
        } catch (error) {
          console.error('Failed to create early bird booking:', error)
          // Reset the ref so we can try again if needed
          earlyBirdBookingAttemptedRef.current = false
        }
      }

      createBooking()
    }
  }, [
    activity,
    state.name,
    state.email,
    state.phoneNumber,
    state.role,
    state.discountType,
    state.discountRegion,
    state.earlyBirdBooking
  ])

  useEffect(() => {
    // allow to create new booking if region was updated
    earlyBirdBookingAttemptedRef.current = false
    const newEarlyBirdBooking = { ...state.earlyBirdBooking }
    if (activity && activity.id) {
      delete newEarlyBirdBooking[activity.id]
      onSetEarlyBirdBooking(newEarlyBirdBooking)
    }
  }, [activity, state.discountRegion])

  let { hasMainUser, hasPartner, mainUserPrices, partnerUserPrices, finalPrice, vatData } = useDiscountCalculations({
    activity,
    client,
    state
  })
  const [mainUserPriorFreeDropinCounts, setMainUserPriorFreeDropinCounts] = useState<
    FreeDropinPriorQualificationCount[]
  >([])
  const [partnerUserPriorFreeDropinCounts, setPartnerUserPriorFreeDropinCounts] = useState<
    FreeDropinPriorQualificationCount[]
  >([])
  const [mainUserIsHelpDancer, setMainUserIsHelpDancer] = useState(false)
  const [partnerUserIsHelpDancer, setPartnerUserIsHelpDancer] = useState(false)
  const normalizedFreeDropinTicketRules = useMemo(
    () => (client?.freeDropinTicketRules || []).map(normalizeFreeDropinRule),
    [client?.freeDropinTicketRules]
  )
  const activeFreeDropinRules = useMemo(
    () => normalizedFreeDropinTicketRules.filter(isRuleActive),
    [normalizedFreeDropinTicketRules]
  )

  const renderFreeDropinQualifiesIcon = (activityToCheck: Activity) => {
    const rule = getRuleForActivity(activeFreeDropinRules, activityToCheck.id)
    if (!rule) return null
    const mainCanEarn = !mainUserIsHelpDancer || ruleAllowsHelpDancers(rule)
    const partnerCanEarn = !hasPartner || !partnerUserIsHelpDancer || ruleAllowsHelpDancers(rule)
    if (!mainCanEarn && !partnerCanEarn) return null
    const tooltip = getGrooverText('registrationStep2FreeDropinQualifiesTooltip', undefined, client?.id)
    return (
      <Tooltip title={tooltip} arrow enterDelay={200}>
        <span style={{ display: 'inline-flex', lineHeight: 0 }} aria-label={tooltip}>
          <CardGiftcard sx={{ fontSize: 16, color: '#70ab37', flexShrink: 0 }} />
        </span>
      </Tooltip>
    )
  }

  const mainUserFreeDropinRewardPreview = useFreeDropinRewardPreview({
    rules: normalizedFreeDropinTicketRules,
    selectedActivities: state.selectedActivities,
    priorQualificationCounts: mainUserPriorFreeDropinCounts,
    isHelpDancer: mainUserIsHelpDancer
  })
  const partnerUserFreeDropinRewardPreview = useFreeDropinRewardPreview({
    rules: normalizedFreeDropinTicketRules,
    selectedActivities: hasPartner ? state.selectedActivities : [],
    priorQualificationCounts: partnerUserPriorFreeDropinCounts,
    isHelpDancer: partnerUserIsHelpDancer
  })

  useEffect(() => {
    if (
      earlyBirdBookings.data &&
      activity &&
      activity.type === 'Event' &&
      activity.id &&
      state.earlyBirdBooking[activity.id]?.bookingId
    ) {
      // exclude current early bird booking from stats
      // (othercase booking last ticket by current user would be counted twice)
      const bookingsWithoutCurrent = (earlyBirdBookings.data as EarlyBirdBooking[]).filter(
        (booking) => booking.id !== state.earlyBirdBooking[activity.id!]?.bookingId
      )
      const _activityWithStats = applyRegistrationStats(activity, bookingsWithoutCurrent as EarlyBirdBooking[])
      console.log('activityWithStats activity.discountConfig', _activityWithStats.discountConfig)
      // update current activity with stats
      setActivityWithStats(_activityWithStats)
      // update activity from state with stats
      onSetSelectedActivities(
        state.selectedActivities.map((_activity) =>
          _activity.id === _activityWithStats.id ? _activityWithStats : _activity
        )
      )
    } else {
      setActivityWithStats(activity)
    }
  }, [earlyBirdBookings, activity, state.earlyBirdBooking])

  // TODO: read subevent type from state

  useEffect(() => {
    const fresh = calculateDiscountData({
      activity: activityWithStats,
      client,
      state
    })
    mainUserPrices = fresh.mainUserPrices
    partnerUserPrices = fresh.partnerUserPrices
    finalPrice = fresh.finalPrice
    vatData = fresh.vatData
  }, [state.mainUserActivePeriods, state.partnerUserActivePeriods, state.singleEventQuantity, state.discountCode?.code])

  useEffect(() => {
    if (!client) return

    let isMounted = true

    setIsLoading(true)
    setError(false)
    const fetchActivePeriodsForUser = async (phone: string, clientId: string) => {
      if (!phone) return []

      const response = await getActivePeriodsForUser({ phone, clientId })
      return response
    }

    const fetchPreviousDiscountsForUser = async (phone: string, clientId: string) => {
      if (!phone) return emptyPreviousDiscounts

      return getUserPreviousDiscounts({ phone, clientId })
    }

    const mainUserActivePeriodsPromise = fetchActivePeriodsForUser(state.phoneNumber, client.id)
    const partnerUserActivePeriodsPromise = state.partnerPhoneNumber
      ? fetchActivePeriodsForUser(state.partnerPhoneNumber, client.id)
      : Promise.resolve([])

    const mainUserPreviousDiscountsPromise = fetchPreviousDiscountsForUser(state.phoneNumber, client.id)
    const partnerUserPreviousDiscountsPromise = state.partnerPhoneNumber
      ? fetchPreviousDiscountsForUser(state.partnerPhoneNumber, client.id)
      : Promise.resolve(emptyPreviousDiscounts)
    const selectedRegistrations = state.selectedActivities.map((selectedActivity) => ({
      activityId: selectedActivity.id,
      periodKey: selectedActivity.periodId || selectedActivity.period || ''
    }))
    const previewUsers = [
      ...(state.phoneNumber ? [{ role: FreeDropinPreviewUserRole.Main, phoneNumber: state.phoneNumber }] : []),
      ...(hasPartner && state.partnerPhoneNumber
        ? [{ role: FreeDropinPreviewUserRole.Partner, phoneNumber: state.partnerPhoneNumber }]
        : [])
    ]
    const freeDropinPreviewPromise =
      previewUsers.length > 0 && selectedRegistrations.length > 0
        ? loadFreeDropinRewardPreview({
            clientId: client.id,
            users: previewUsers,
            selectedRegistrations,
            ruleRevisions: (client.freeDropinTicketRules || []).map((rule) => ({
              ruleId: rule.id,
              revision: rule.revision ?? 1
            }))
          }).catch((error) => {
            console.warn('Error fetching prior free drop-in qualifications', error)
            return { users: [] }
          })
        : Promise.resolve({ users: [] })

    Promise.all([
      mainUserActivePeriodsPromise,
      partnerUserActivePeriodsPromise,
      mainUserPreviousDiscountsPromise,
      partnerUserPreviousDiscountsPromise,
      freeDropinPreviewPromise
    ])
      .then(
        ([
          mainUserActivePeriods,
          partnerUserActivePeriods,
          mainUserPreviousDiscounts,
          partnerUserPreviousDiscounts,
          freeDropinPreview
        ]) => {
          if (isMounted) {
            onSetMainUserActivePeriods(mainUserActivePeriods)
            onSetPartnerUserActivePeriods(partnerUserActivePeriods)
            onSetMainUserPreviousDiscounts(mainUserPreviousDiscounts)
            onSetPartnerUserPreviousDiscounts(partnerUserPreviousDiscounts)
            const mainPreview = freeDropinPreview.users.find((user) => user.role === FreeDropinPreviewUserRole.Main)
            const partnerPreview = freeDropinPreview.users.find(
              (user) => user.role === FreeDropinPreviewUserRole.Partner
            )
            setMainUserPriorFreeDropinCounts(mainPreview?.priorQualificationCounts || [])
            setPartnerUserPriorFreeDropinCounts(partnerPreview?.priorQualificationCounts || [])
            setMainUserIsHelpDancer(mainPreview?.isHelpDancer === true)
            setPartnerUserIsHelpDancer(partnerPreview?.isHelpDancer === true)
          }
        }
      )
      .catch((err) => {
        if (isMounted) {
          setError(true)
          console.warn('Error fetching user pre-registration data', err)
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [client, hasPartner, state.phoneNumber, state.partnerPhoneNumber, state.selectedActivities])

  // Watch for changes in partnerCheckboxes and update state.registeredPartners
  useEffect(() => {
    const subscription = formProps.watch((value, { name }) => {
      if (name?.startsWith('partnerCheckboxes.')) {
        const partners = Object.entries(value.partnerCheckboxes || {})
          // only those that are checked
          .filter(([, isChecked]) => isChecked)
          .reduce(
            (acc, [key]) => {
              acc[key] = value.partners?.[key] || ''
              return acc
            },
            {} as Record<string, string>
          )
        onSetEnrollmentRegisteredPartners(partners)
      }
    })
    return () => subscription.unsubscribe()
  }, [formProps.watch])

  const onOpenStep = () => {
    if (registerActiveStep > 2 || state.isRegistrationCompleted || !!state.obfuscatedEnrollment?.mainUser)
      onOpenStepTwo()
  }

  // Auto-populate registeredPartners with partner data from Step One if partner exists
  useEffect(() => {
    if (hasPartner && state.partnerName && state.selectedActivities.length > 0) {
      // Check if we already have the partner data to prevent infinite loop
      const currentPartnerData = state.registeredPartners || {}
      const hasPartnerData = state.selectedActivities.some(
        (activity) => currentPartnerData[activity.id] === state.partnerName
      )

      if (!hasPartnerData) {
        const partnerData: Record<string, string> = {}

        // Add partner to all selected activities
        state.selectedActivities.forEach((activity) => {
          partnerData[activity.id] = state.partnerName!
        })

        onSetEnrollmentRegisteredPartners(partnerData)
      }
    }
  }, [hasPartner, state.partnerName, state.selectedActivities, onSetEnrollmentRegisteredPartners])

  useEffect(() => {
    if (finalPrice) setFinalPrice(finalPrice + ' ' + currency)
  }, [finalPrice])

  return registerActiveStep !== 2 ? (
    <ClosedContainer onClick={onOpenStep}>
      <Box fullWidth direction="row" align="center" gap="15px">
        {(registerActiveStep > 1 || state.isRegistrationCompleted || state.obfuscatedEnrollment?.mainUser?.name) &&
        hasMainUser ? (
          <img src={iconCompleted} width={24} height={24} />
        ) : null}
        <GrooverText textKey="registrationHeadersAndStatusHeaderSelectedCourses" />
      </Box>
    </ClosedContainer>
  ) : (
    <OpenContainer>
      {error && (
        <Box fullWidth direction="row" align="center" backgroundColor="#fef2f2" gap="15px" style={{ padding: '10px' }}>
          <Error sx={{ color: 'red' }} />
          <span style={{ color: 'red' }}>{getGrooverText('registrationValidationsErrorFailedUserData')}</span>
        </Box>
      )}
      {/* -- BEGIN role selection */}
      {shouldShowRoleSelection && (
        <>
          {roleValidationError && (
            <Box
              fullWidth
              direction="row"
              align="center"
              backgroundColor="#fef2f2"
              gap="15px"
              style={{ padding: '10px' }}
            >
              <Error sx={{ color: 'red' }} />
              <span style={{ color: 'red' }}>{getGrooverText('registrationValidationsErrorRoleRequired')}</span>
            </Box>
          )}
          {(() => {
            const { leadersClosed, followersClosed } = getRoleRestrictionsForActivity(activity)
            if (!leadersClosed && !followersClosed) return null

            return (
              <RoleRestrictionInfoBox fullWidth>
                {leadersClosed && <GrooverText textKey="registrationStep2RoleClosedForLeadersInfo" />}
                {followersClosed && <GrooverText textKey="registrationStep2RoleClosedForFollowersInfo" />}
              </RoleRestrictionInfoBox>
            )
          })()}
          <Box fullWidth direction="row" align="center" gap="15px">
            <Box direction="row" gap="15px">
              {state.isRegistrationCompleted ||
                (!!state.obfuscatedEnrollment?.mainUser && <img src={iconCompleted} width={24} height={24} />)}
              <GrooverText textKey="registrationHeadersAndStatusHeaderRoleSelectionSection" />
            </Box>
          </Box>
          <Box
            id="user-types-container"
            fullWidth
            direction="row"
            changeDirectionOnMobile
            align="center"
            justify="flex-start"
            top
            spacing={EIGHT_PX}
          >
            <UserTypeBox id="user-types-roles" spacing={EIGHT_PX} alignSelf="flex-start" hasError={isRoleMissing}>
              <FormControl error={isRoleMissing}>
                <StyledRadioGroup
                  row
                  value={(resolvedRole || '') as string}
                  aria-invalid={isRoleMissing ? 'true' : undefined}
                  aria-describedby={isRoleMissing ? 'registration-role-required-error' : undefined}
                  onChange={(e) => {
                    if (
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      state.obfuscatedEnrollment?.mainUser?.phone
                    )
                      return

                    const { leadersClosed, followersClosed } = getRoleRestrictionsForActivity(activity)
                    const nextRole = e.target.value as DanceRoles
                    if (
                      (leadersClosed && nextRole === DanceRoles.Leader) ||
                      (followersClosed && nextRole === DanceRoles.Follower) ||
                      ((leadersClosed || followersClosed) && nextRole === DanceRoles.Both)
                    ) {
                      return
                    }

                    onSetRole(e.target.value as DanceRoles)
                  }}
                >
                  <StyledFormControlLabel
                    value={DanceRoles.Leader}
                    control={<StyledRadio />}
                    label={getGrooverText('registrationStep1AttendeesRegRoleLabelLeader')}
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.mainUser?.phone ||
                      getRoleRestrictionsForActivity(activity).leadersClosed
                    }
                  />
                  <StyledFormControlLabel
                    value={DanceRoles.Follower}
                    control={<StyledRadio />}
                    label={getGrooverText('registrationStep1AttendeesRegRoleLabelFollower')}
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.mainUser?.phone ||
                      getRoleRestrictionsForActivity(activity).followersClosed
                    }
                  />
                  {!!client?.allowBothForCouples && (
                    <StyledFormControlLabel
                      value={DanceRoles.Both}
                      control={<StyledRadio />}
                      label={getGrooverText('registrationStep1AttendeesRegRoleLabelBoth')}
                      disabled={
                        state.isRegistrationCompleted ||
                        state.isRegistrationSubmitted ||
                        !!state.obfuscatedEnrollment?.mainUser?.phone ||
                        getRoleRestrictionsForActivity(activity).leadersClosed ||
                        getRoleRestrictionsForActivity(activity).followersClosed
                      }
                    />
                  )}
                </StyledRadioGroup>
                {isRoleMissing && (
                  <Box
                    id="registration-role-required-error"
                    fullWidth
                    direction="row"
                    align="center"
                    gap={SIX_PX}
                    style={{ padding: '8px 12px' }}
                  >
                    <Error sx={{ color: '#dc2626', width: 18, height: 18 }} />
                    <span style={{ color: '#dc2626', fontWeight: 600 }}>
                      {getGrooverText('registrationValidationsErrorRoleRequired')}
                    </span>
                  </Box>
                )}
              </FormControl>
            </UserTypeBox>
          </Box>
        </>
      )}
      {/* -- END role selection */}
      {/* Package Section - Show above selected activities, below role selection */}
      {activity?.eventType === 'Parentevent' &&
        allActivities &&
        allActivities.length > 0 &&
        (() => {
          const packages = getPackagesForParentEvent(client, activity.id, allActivities)
          if (packages.length > 0) {
            return (
              <SectionContainer fullWidth>
                <SectionHeader fullWidth direction="row" align="center">
                  <GrooverText
                    textKey="registrationStep2SubeventsPackageSelection"
                    style={{ color: '#000', fontSize: '16px' }}
                  />
                </SectionHeader>
                <PackagePillsContainer fullWidth direction="row">
                  {packages.map((pkg) => {
                    // Create a unique identifier for this package
                    const packageId = pkg.name || `package-${pkg.courses?.sort().join('-')}`
                    // Check if this package is selected using the tracked package ID
                    const isPackageSelected = selectedPackageId === packageId

                    const priceInCurrency = Math.round(pkg.calculatedPrice / 100)
                    const packageName = pkg.name || `Package (${pkg.courses?.length || 0} subevents)`

                    return (
                      <PackagePill
                        key={pkg.name || `package-${pkg.courses?.join('-')}`}
                        isSelected={isPackageSelected}
                        onClick={() => {
                          if (!state.isRegistrationCompleted && !state.isRegistrationSubmitted) {
                            handlePackageSelect(pkg)
                          }
                        }}
                      >
                        <Box
                          direction="column"
                          align="flex-start"
                          gap={FOUR_PX}
                          fullWidth
                          style={{ position: 'relative' }}
                        >
                          <Box direction="row" align="center" justify="space-between" fullWidth>
                            <span style={{ fontWeight: 600, fontSize: '16px' }}>{packageName}</span>
                          </Box>
                          <Box direction="row" align="center" justify="space-between" fullWidth>
                            <span style={{ color: '#7c3aed', fontWeight: 600, fontSize: '16px' }}>
                              {priceInCurrency.toLocaleString('sv-SE')} {currency}
                            </span>
                          </Box>
                          {isPackageSelected && (
                            <CheckCircle
                              sx={{
                                color: '#7c3aed',
                                fontSize: 20,
                                position: 'absolute',
                                bottom: 8,
                                right: 8
                              }}
                            />
                          )}
                        </Box>
                      </PackagePill>
                    )
                  })}
                </PackagePillsContainer>
              </SectionContainer>
            )
          }
          return null
        })()}
      {hasMainUser || !!state.obfuscatedEnrollment?.mainUser?.phone ? (
        <>
          <SelectedCourses fullWidth direction="row" align="flex-start" justify="space-between">
            <Box fullWidth>
              <Box fullWidth fullHeight gap={TEN_PX} top spacing={SIXTEEN_PX}>
                {!state.obfuscatedEnrollment?.activities &&
                  (() => {
                    // Special handling for subevents - show grouped by day
                    if (activity?.eventType === 'Parentevent' && allActivities && allActivities.length > 0) {
                      const selectedActivities = state?.selectedActivities || []
                      const groupedByDay = groupActivitiesByDay(allActivities)

                      return (
                        <SectionContainer fullWidth>
                          <SectionHeader fullWidth direction="row" align="center">
                            <GrooverText
                              textKey="registrationStep2SubeventsSelectedActivities"
                              style={{ color: '#000', fontSize: '16px' }}
                            />
                          </SectionHeader>

                          {groupedByDay.map((dayGroup, dayIndex) => {
                            const dayColor = getDayColor(dayIndex)
                            const dayShortUpper = dayGroup.dayShort.toUpperCase()

                            return (
                              <Box
                                key={dayGroup.date}
                                fullWidth
                                direction="column"
                                gap={EIGHT_PX}
                                style={{ marginBottom: '24px' }}
                              >
                                <DayHeader dayColor={dayColor} fullWidth>
                                  {dayShortUpper} {dayGroup.dateFormatted}
                                </DayHeader>

                                {dayGroup.activities.map((subevent) => {
                                  const isDisabled =
                                    subevent.registrationStatus === ActivityRegistrationStatus.Closed ||
                                    subevent.registrationStatus === ActivityRegistrationStatus.Full
                                  const isSelected = selectedActivities.some((a) => a.id === subevent.id) || false

                                  return (
                                    <ActivityItem key={subevent.id} fullWidth style={{ opacity: isDisabled ? 0.6 : 1 }}>
                                      <ActivityRowContainer>
                                        {/* Checkbox column */}
                                        <CheckboxColumn>
                                          <input
                                            type="checkbox"
                                            id={`subevent-${subevent.id}`}
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={(e) => {
                                              if (e.target.checked && !isDisabled) {
                                                handleAddActivity(subevent)
                                              } else if (!e.target.checked) {
                                                handleRemoveActivity(subevent)
                                              }
                                            }}
                                            style={{
                                              width: '18px',
                                              height: '18px',
                                              cursor: isDisabled ? 'not-allowed' : 'pointer'
                                            }}
                                          />
                                        </CheckboxColumn>
                                        {/* Content column */}
                                        <ContentColumn>
                                          {/* First row: Activity Name */}
                                          <ActivityRow style={{ marginBottom: '8px' }}>
                                            <ActivityInfo>
                                              <Box style={{ fontWeight: 500 }}>{subevent.name}</Box>
                                            </ActivityInfo>
                                          </ActivityRow>
                                          {/* Second row: Date/Time and Price */}
                                          <ActivityRow style={{ justifyContent: 'space-between' }}>
                                            <ActivityTime>
                                              {dayjs(subevent.startDate).format('D/M')}{' '}
                                              {getWeekdayAbbreviation(subevent.startDate)} {subevent.time}
                                            </ActivityTime>
                                            <ActivityPrice>
                                              {Math.round((subevent.price || 0) / 100).toLocaleString('sv-SE')}{' '}
                                              {currency}
                                            </ActivityPrice>
                                          </ActivityRow>
                                          {isDisabled && subevent.registrationStatus && (
                                            <Box style={{ marginTop: '8px' }}>
                                              <ResponsiveActivityStatusBadge
                                                registrationStatus={subevent.registrationStatus}
                                              />
                                            </Box>
                                          )}
                                        </ContentColumn>
                                      </ActivityRowContainer>
                                    </ActivityItem>
                                  )
                                })}
                              </Box>
                            )
                          })}
                        </SectionContainer>
                      )
                    }

                    // Regular logic for non-subevents
                    return Object.entries(
                      state?.selectedActivities?.reduce(
                        (acc, activity) => {
                          const key = activity.group || activity.id
                          return {
                            ...acc,
                            [key]: [...(acc[key] || []), activity]
                          }
                        },
                        {} as Record<string, Activity[]>
                      )
                    ).map(([group, groupEntries]) => (
                      <Box
                        id="activity-list-item-container"
                        key={`group-${group}`}
                        fullWidth
                        direction="row"
                        align="flex-start"
                        justify="flex-start"
                        left
                        spacing={EIGHT_PX}
                        gap={EIGHT_PX}
                      >
                        <Box
                          id="activivy-list-item-selector"
                          direction="row"
                          align="center"
                          gap={TWO_PX}
                          top
                          spacing={TWO_PX}
                        >
                          {(!state.isRegistrationSubmitted || !!state.obfuscatedEnrollment?.mainUser) &&
                          !groupEntries.some((a) => a.eventType && ['SingleEvent', 'Event'].includes(a.eventType)) ? (
                            <InvisibleButton
                              onClick={() => {
                                // Remove all activities in this group
                                groupEntries.forEach((activity) => handleRemoveActivity(activity))
                              }}
                            >
                              <img src={removeCourseIcon} width={16} height={16} />
                            </InvisibleButton>
                          ) : null}
                        </Box>
                        <Box id="activity-list-main-container" fullWidth>
                          <Box id="activity-list-item-header" fullWidth>
                            <span>{groupEntries[0]?.name}</span>
                          </Box>
                          {groupEntries.map((selectedActivity) => (
                            <Box key={`activity-${selectedActivity.id}`} id="activity-list-main-container" fullWidth>
                              <Box direction="row" align="center" justify="space-between" gap={FOUR_PX} fullWidth>
                                <Box direction="row" align="center" gap={FOUR_PX}>
                                  <Box id="activity-list-startdate" direction="row" align="center" gap={FOUR_PX}>
                                    <StartDate fullPadding spacing={FOUR_PX}>
                                      <span>{dayjs(selectedActivity?.startDate).format('D/M')}</span>
                                    </StartDate>
                                  </Box>
                                  <Box direction="row" gap={FOUR_PX}>
                                    <span>{getWeekdayAbbreviation(selectedActivity?.startDate)}</span>
                                    <span>{selectedActivity?.time}</span>
                                    {groupEntries.length > 1 &&
                                      !state.isRegistrationCompleted &&
                                      (!disableAdditionalActivities ||
                                        allowParentEventSelectionWhenDisableAdditional) && (
                                        <InvisibleButton onClick={() => handleRemoveActivity(selectedActivity)}>
                                          <img src={removeCourseIcon} width={16} height={16} />
                                        </InvisibleButton>
                                      )}
                                  </Box>
                                  {/* Quantity dropdown for SingleEvent */}
                                  {selectedActivity?.eventType === 'SingleEvent' && !state.isRegistrationCompleted && (
                                    <Box direction="row" align="center" gap={FOUR_PX} style={{ marginLeft: '16px' }}>
                                      <GrooverText textKey="registrationStep2ActivitiesSingleEventQuantityLabel" />
                                      <Controller
                                        control={formProps.control}
                                        name="singleEventQuantity"
                                        render={({ field }) => (
                                          <FormControl size="small" sx={{ minWidth: 60 }}>
                                            <Select
                                              {...field}
                                              value={field.value}
                                              onChange={(e) => {
                                                field.onChange(e.target.value)
                                                if (onSetSingleEventQuantity) {
                                                  onSetSingleEventQuantity(Number(e.target.value))
                                                }
                                              }}
                                              disabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
                                            >
                                              {Array.from(
                                                {
                                                  length: Math.min(selectedActivity.maxAllowedParticipants || 1, 10)
                                                },
                                                (_, i) => i + 1
                                              ).map((num) => (
                                                <MenuItem key={num} value={num}>
                                                  {num}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        )}
                                      />
                                    </Box>
                                  )}
                                </Box>
                                <Box direction="row" align="center" gap="8px">
                                  {renderFreeDropinQualifiesIcon(selectedActivity)}
                                  <BadgeLinkRow>
                                    {isActivityClosedOrFull(selectedActivity.registrationStatus) && (
                                      <ResponsiveActivityStatusBadge
                                        registrationStatus={selectedActivity.registrationStatus}
                                      />
                                    )}
                                    <Link
                                      style={{ paddingTop: FOUR_PX }}
                                      to={
                                        client?.slug && selectedActivity.seoLink
                                          ? buildRoute(
                                              RoutePath.ACTIVITY_DETAIL_SEO,
                                              {
                                                clientSlug: client.slug,
                                                activitySlug: selectedActivity.seoLink
                                              },
                                              langQuery ? { lang: langQuery } : undefined
                                            )
                                          : buildRoute(
                                              RoutePath.ACTIVITY_DETAIL,
                                              { activityId: selectedActivity.id || '' },
                                              langQuery ? { lang: langQuery } : undefined
                                            )
                                      }
                                      target="_blank"
                                    >
                                      <OpenInNew sx={{ width: 20, height: 20, color: '#DFDFDF' }} />{' '}
                                    </Link>
                                  </BadgeLinkRow>
                                </Box>
                              </Box>
                              {registrationTypeCouple &&
                                !selectedActivity.singleRegistration &&
                                canRegisterPartnersForActivities && (
                                  <PartnerBox gap={FOUR_PX} fullWidth>
                                    <PartnerBoxCheckbox direction="row">
                                      <Controller
                                        control={formProps.control}
                                        name={`partnerCheckboxes.${selectedActivity.id}`}
                                        defaultValue={false}
                                        render={({ field: { onChange, value, ...field } }) => (
                                          <>
                                            <input
                                              type="checkbox"
                                              id={`activity-${selectedActivity.id}`}
                                              checked={value}
                                              onChange={(e) => onChange(e.target.checked)}
                                              {...field}
                                            />
                                            <label htmlFor={`activity-${selectedActivity.id}`}>
                                              <GrooverText textKey="registrationStep2ActivitiesPartnerCheckboxLabel" />
                                            </label>
                                          </>
                                        )}
                                      />
                                    </PartnerBoxCheckbox>
                                    {formProps.watch(`partnerCheckboxes.${selectedActivity.id}`) && (
                                      <>
                                        <PartnerBoxInput direction="row" fullWidth>
                                          <Controller
                                            control={formProps.control}
                                            name={`partners.${selectedActivity.id}`}
                                            render={({ field, fieldState }) => (
                                              <TextFieldWithLabelForm
                                                {...field}
                                                value={field.value || ''}
                                                fullWidth
                                                fieldState={fieldState}
                                                placeholder={getGrooverText(
                                                  'registrationStep2ActivitiesPartnerNamePlaceholder'
                                                )}
                                              />
                                            )}
                                          />
                                        </PartnerBoxInput>
                                        <PartnerInfoNotice>
                                          <GrooverText textKey="registrationStep2ActivitiesPartnerRegistrationNotice" />
                                        </PartnerInfoNotice>
                                      </>
                                    )}
                                  </PartnerBox>
                                )}
                              {selectedActivity.allowChildRegistration && onSetChildrenInput && (
                                <ChildBox gap={FOUR_PX} fullWidth>
                                  <GrooverText
                                    textKey="registrationHeadersAndStatusHeaderChild"
                                    style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}
                                  />
                                  <ChildFieldsRow direction="row" gap={EIGHT_PX} fullWidth>
                                    <ChildInput
                                      type="text"
                                      hasError={!!childValidationErrors[selectedActivity.id]?.name}
                                      value={state.childrenInput[selectedActivity.id]?.name || ''}
                                      onChange={(e) => {
                                        setChildValidationErrors((prev) => ({
                                          ...prev,
                                          [selectedActivity.id]: { ...prev[selectedActivity.id], name: false }
                                        }))
                                        onSetChildrenInput({
                                          ...state.childrenInput,
                                          [selectedActivity.id]: {
                                            name: e.target.value,
                                            birthYear: state.childrenInput[selectedActivity.id]?.birthYear || ''
                                          }
                                        })
                                      }}
                                      placeholder={getGrooverText('registrationStep1ChildNameHelper')}
                                      disabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
                                    />
                                    <ChildInput
                                      type="text"
                                      hasError={!!childValidationErrors[selectedActivity.id]?.birthYear}
                                      value={state.childrenInput[selectedActivity.id]?.birthYear || ''}
                                      onChange={(e) => {
                                        setChildValidationErrors((prev) => ({
                                          ...prev,
                                          [selectedActivity.id]: { ...prev[selectedActivity.id], birthYear: false }
                                        }))
                                        onSetChildrenInput({
                                          ...state.childrenInput,
                                          [selectedActivity.id]: {
                                            name: state.childrenInput[selectedActivity.id]?.name || '',
                                            birthYear: e.target.value
                                          }
                                        })
                                      }}
                                      placeholder={getGrooverText('registrationStep1ChildBirthYearHelper')}
                                      disabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
                                    />
                                  </ChildFieldsRow>
                                  {childValidationErrors[selectedActivity.id]?.name && (
                                    <ChildErrorText>{getGrooverText('validationChildNameRequired')}</ChildErrorText>
                                  )}
                                  {childValidationErrors[selectedActivity.id]?.birthYear && (
                                    <ChildErrorText>{getGrooverText('validationChildBirthYearInvalid')}</ChildErrorText>
                                  )}
                                </ChildBox>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))
                  })()}

                {!!state?.obfuscatedEnrollment?.activities &&
                  Object.entries(
                    state?.obfuscatedEnrollment?.activities?.reduce(
                      (acc, activity) => {
                        const key = activity.group || activity.id
                        return {
                          ...acc,
                          [key]: [...(acc[key] || []), activity]
                        }
                      },
                      {} as Record<string, Activity[]>
                    )
                  ).map(([group, groupEntries]) => (
                    <Box
                      id="activity-list-item-container"
                      key={`group-${group}`}
                      fullWidth
                      direction="row"
                      align="flex-start"
                      justify="flex-start"
                      left
                      spacing={EIGHT_PX}
                      gap={EIGHT_PX}
                    >
                      <Box
                        id="activivy-list-item-selector"
                        direction="row"
                        align="center"
                        gap={TWO_PX}
                        top
                        spacing={TWO_PX}
                      >
                        {!state.isRegistrationSubmitted ||
                          (!!state.obfuscatedEnrollment?.mainUser && (
                            <InvisibleButton
                              onClick={() => {
                                // Remove all activities in this group
                                groupEntries.forEach((activity) => handleRemoveActivity(activity))
                              }}
                            >
                              <img src={removeCourseIcon} width={16} height={16} />
                            </InvisibleButton>
                          ))}
                      </Box>
                      <Box id="activity-list-main-container" fullWidth>
                        <Box id="activity-list-item-header" fullWidth>
                          <span>{groupEntries[0]?.name}</span>
                        </Box>
                        {groupEntries.map((selectedActivity) => (
                          <Box key={`activity-${selectedActivity.id}`} id="activity-list-main-container" fullWidth>
                            <Box direction="row" align="center" justify="space-between" gap={FOUR_PX} fullWidth>
                              <Box direction="row" align="center" gap={FOUR_PX}>
                                <Box id="activity-list-startdate" direction="row" align="center" gap={FOUR_PX}>
                                  <StartDate fullPadding spacing={FOUR_PX}>
                                    <span>{dayjs(selectedActivity?.startDate).format('D/M')}</span>
                                  </StartDate>
                                </Box>
                                <Box direction="row" gap={FOUR_PX}>
                                  <span>{getWeekdayAbbreviation(selectedActivity?.startDate)}</span>
                                  <span>{selectedActivity?.time}</span>
                                  {groupEntries.length > 1 &&
                                    !state.isRegistrationCompleted &&
                                    (!disableAdditionalActivities ||
                                      allowParentEventSelectionWhenDisableAdditional) && (
                                      <InvisibleButton onClick={() => handleRemoveActivity(selectedActivity)}>
                                        <img src={removeCourseIcon} width={16} height={16} />
                                      </InvisibleButton>
                                    )}
                                </Box>
                              </Box>
                              <Box direction="row" align="center" gap="8px">
                                {renderFreeDropinQualifiesIcon(selectedActivity)}
                                <BadgeLinkRow>
                                  {isActivityClosedOrFull(selectedActivity.registrationStatus) && (
                                    <ResponsiveActivityStatusBadge
                                      registrationStatus={selectedActivity.registrationStatus}
                                    />
                                  )}
                                  <Link
                                    style={{ paddingTop: FOUR_PX }}
                                    to={
                                      client?.slug && selectedActivity.seoLink
                                        ? buildRoute(
                                            RoutePath.ACTIVITY_DETAIL_SEO,
                                            {
                                              clientSlug: client.slug,
                                              activitySlug: selectedActivity.seoLink
                                            },
                                            langQuery ? { lang: langQuery } : undefined
                                          )
                                        : buildRoute(
                                            RoutePath.ACTIVITY_DETAIL,
                                            { activityId: selectedActivity.id || '' },
                                            langQuery ? { lang: langQuery } : undefined
                                          )
                                    }
                                    target="_blank"
                                  >
                                    <OpenInNew sx={{ width: 20, height: 20, color: '#DFDFDF' }} />{' '}
                                  </Link>
                                </BadgeLinkRow>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Box>

            {activity && isActivityFree(activity, state.selectedActivities, allActivities) ? (
              <Box fullWidth fullPadding spacing={EIGHT_PX} gap="8px">
                <GrooverText textKey="registrationStep2FreeEvent" />
                <FreeDropinRewardPreview preview={mainUserFreeDropinRewardPreview} client={client} />
                {hasPartner && <FreeDropinRewardPreview preview={partnerUserFreeDropinRewardPreview} client={client} />}
              </Box>
            ) : (
              <PriceContainer
                currency={currency}
                mainUserPrices={mainUserPrices}
                partnerUserPrices={partnerUserPrices}
                hasPartner={hasPartner}
                finalPrice={finalPrice}
                vatData={vatData}
                state={state}
                isLoading={isLoading}
                client={client}
                mainUserFreeDropinRewardPreview={mainUserFreeDropinRewardPreview}
                partnerUserFreeDropinRewardPreview={partnerUserFreeDropinRewardPreview}
              />
            )}
          </SelectedCourses>

          {!state.isRegistrationCompleted &&
            allActivities &&
            activity?.eventType !== 'SingleEvent' &&
            (() => {
              const filteredActivities = allActivities.filter(
                (activity) => !state?.selectedActivities?.some((selected) => selected.id === activity.id)
              )
              return filteredActivities.length > 0 &&
                !state.isRegistrationCompleted &&
                !state.isRegistrationSubmitted &&
                !state.obfuscatedEnrollment?.mainUser ? (
                <Box fullWidth>
                  {!disableAdditionalActivities && showAddSection && (
                    <SectionAddMoreActivities id="SectionAddMoreActivities" fullWidth>
                      {getAvailableTabsAndContent(filteredActivities)}
                    </SectionAddMoreActivities>
                  )}
                </Box>
              ) : null
            })()}
        </>
      ) : (
        <Box top>
          <GrooverText textKey="registrationStep2EnterDetailsInStep1" />
        </Box>
      )}
      {showCommentInput && (
        <Controller
          control={formProps.control}
          name="comment"
          render={({ field, fieldState }) => (
            <TextFieldWithLabelForm
              {...field}
              value={field.value}
              fullWidth
              fieldState={fieldState}
              placeholder={getGrooverText('registrationStep3ActivitiesCommentHelperText')}
            />
          )}
        />
      )}
      {(showDiscountCodeInput || isDiscountCodeValid) && (
        <Box fullWidth direction="row" align="flex-start" gap={EIGHT_PX}>
          <Box fullWidth style={{ position: 'relative' }}>
            <Controller
              control={formProps.control}
              name="discountCode"
              render={({ field, fieldState }) => (
                <TextFieldWithLabelForm
                  {...field}
                  value={field.value}
                  disabled={isDiscountCodeValid}
                  fullWidth
                  fieldState={fieldState}
                  placeholder={getGrooverText('registrationRegDiscountCodeHelper')}
                  error={!!discountCodeError || !!fieldState.error}
                  helperText={discountCodeError || fieldState.error?.message}
                />
              )}
            />
            {/* Close button - show when input is visible but not validated, or when validated */}
            {(showDiscountCodeInput || isDiscountCodeValid) && (
              <Box
                onClick={isDiscountCodeValid ? handleClearDiscountCode : handleCloseDiscountCodeInput}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Close sx={{ fontSize: 16, color: '#666' }} />
              </Box>
            )}
          </Box>
          {!isDiscountCodeValid && (
            <ApplyDiscountCodeButton
              isDisabled={
                isVerifyingDiscountCode ||
                isDiscountCodeValid ||
                state.isRegistrationSubmitted ||
                !formProps.watch('discountCode')?.trim()
              }
              direction="row"
              align="center"
              onClick={
                !isVerifyingDiscountCode && !state.isRegistrationSubmitted && formProps.watch('discountCode')?.trim()
                  ? handleVerifyDiscountCode
                  : undefined
              }
            >
              {isVerifyingDiscountCode
                ? getGrooverText('registrationCtactaVerifying')
                : getGrooverText('registrationCtactaApplyCode')}
            </ApplyDiscountCodeButton>
          )}
        </Box>
      )}
      <Box
        fullWidth
        direction={isMobile ? 'column' : 'row'}
        justify="space-between"
        top
        spacing={EIGHT_PX}
        gap={EIGHT_PX}
        changeDirectionOnMobile
      >
        <Box direction="row" gap={SIX_PX} bottom={isMobile} changeDirectionOnMobile>
          {!showAddSection &&
            !state.obfuscatedEnrollment.mainUser?.phone &&
            activity?.eventType !== 'SingleEvent' &&
            activity?.eventType !== 'Parentevent' &&
            activity?.eventType !== 'Event' &&
            !disableAdditionalActivities && (
              <AddMoreButton
                isMobile={isMobile}
                isDisabled={state.isRegistrationSubmitted}
                direction="row"
                align="center"
                onClick={!state.isRegistrationSubmitted ? () => setShowAddSection(true) : undefined}
              >
                <Add sx={{ fontSize: 20, marginRight: isMobile ? '5px' : '8px' }} />
                <GrooverText textKey="registrationCtactaAddMoreCourses" />
              </AddMoreButton>
            )}
          {!state.obfuscatedEnrollment.mainUser?.phone && (
            <AddCommentButton
              isDisabled={state.isRegistrationSubmitted}
              direction="row"
              align="center"
              onClick={!state.isRegistrationSubmitted ? () => setShowCommentInput(!showCommentInput) : undefined}
            >
              <Add sx={{ fontSize: 20, marginRight: isMobile ? '5px' : '8px' }} />
              <GrooverText textKey="registrationCtactaAddComment" />
            </AddCommentButton>
          )}
          {!state.obfuscatedEnrollment.mainUser?.phone &&
            hasPricedSelection &&
            client?.discounts.some((discount) => discount.type === 'code') && (
              <AddDiscountCodeButton
                isDisabled={state.isRegistrationSubmitted}
                direction="row"
                align="center"
                onClick={
                  !state.isRegistrationSubmitted ? () => setShowDiscountCodeInput(!showDiscountCodeInput) : undefined
                }
              >
                <Add sx={{ fontSize: 20, marginRight: isMobile ? '5px' : '8px' }} />
                <GrooverText textKey="registrationCtactaAddDiscountCode" />
              </AddDiscountCodeButton>
            )}
        </Box>
        {!state.isRegistrationCompleted ? (
          <Box alignSelf="flex-end">
            <ContinueButton
              onClick={handleNext}
              disabled={
                (!state.obfuscatedEnrollment?.activities && !hasMainUser) ||
                state.selectedActivities.length <= 0 ||
                isLoading ||
                (shouldShowRoleSelection && !resolvedRole)
              }
              isDisabled={
                (!state.obfuscatedEnrollment?.activities && !hasMainUser) ||
                state.selectedActivities.length <= 0 ||
                isLoading ||
                (shouldShowRoleSelection && !resolvedRole)
              }
            >
              <GrooverText textKey="registrationCtactaContinue" />
              <img src={iconArrow} width={16} height={16} />
            </ContinueButton>
          </Box>
        ) : null}
      </Box>
    </OpenContainer>
  )
}

const addMoreOrCommentPadding = '8px 16px'

const AddCommentButton = styled(Box)<{ isDisabled?: boolean; isMobile?: boolean }>`
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  border: 1px solid #aaa;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  background: ${(props) => (props.isDisabled ? '#f5f5f5' : '#fff')};
  border-radius: ${BorderRadixes.button};
  padding: ${addMoreOrCommentPadding};
  align-self: flex-start;
  min-height: 36px;
  box-sizing: border-box;

  &:hover {
    background-color: #f5f5f5;
  }
`

const AddDiscountCodeButton = styled(Box)<{ isDisabled?: boolean; isMobile?: boolean }>`
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  border: 1px solid #aaa;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  background: ${(props) => (props.isDisabled ? '#f5f5f5' : '#fff')};
  border-radius: ${BorderRadixes.button};
  padding: ${addMoreOrCommentPadding};
  align-self: flex-start;
  min-height: 36px;
  box-sizing: border-box;

  &:hover {
    background-color: #f5f5f5;
  }
`

const ApplyDiscountCodeButton = styled(Box)<{ isDisabled?: boolean }>`
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  border: 1px solid ${(props) => (props.isDisabled ? '#aaa' : '#2563eb')};
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  background: ${(props) => (props.isDisabled ? '#f5f5f5' : '#2563eb')};
  color: ${(props) => (props.isDisabled ? '#000' : '#fff')};
  border-radius: ${BorderRadixes.button};
  padding: 8px 16px;
  margin: 6px 0;
  white-space: nowrap;
  min-width: 80px;
  justify-content: center;
  font-weight: 500;

  &:hover {
    background-color: ${(props) => (props.isDisabled ? '#f5f5f5' : '#1d4ed8')};
  }
`

const AddMoreButton = styled(Box)<{ isDisabled?: boolean; isMobile?: boolean }>`
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  border: 1px solid #aaa;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  background: ${(props) => (props.isDisabled ? '#f5f5f5' : '#fff')};
  border-radius: ${BorderRadixes.button};
  padding: ${addMoreOrCommentPadding};
  align-self: flex-start;
  min-height: 36px;
  box-sizing: border-box;

  &:hover {
    background-color: #f5f5f5;
  }
`

const AddActivitiesHeader = styled(Box)`
  display: flex;
  align-items: center;
  padding: 6px 0 6px 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: #2563eb;
  border-left: 5px solid #2563eb;
  background: none;
  margin-bottom: 6px;
  padding-left: 18px;
`

const CloseButton = styled(Box)`
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #f5f7fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 4px 12px;
  margin-bottom: 12px;
  width: 100%;
  box-sizing: border-box;

  .MuiInputBase-root {
    font-size: 14px;
    color: #333;
  }
`

const SectionAddMoreActivities = styled(Box)`
  border-radius: 12px;
  background: #fff;
  border: 1px solid #eee;
  width: 100%;
  padding: 16px;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.05);
`

const ActivityItemTitle = styled(Box)`
  width: 100%;
`
const ActivityListItem = styled(Box)`
  &:hover {
    border-radius: 6px;
    background-color: rgba(161, 161, 161, 0.22);
  }
`

const SelectedCourses = styled(Box)`
  gap: 24px;
  @media (max-width: 767px) {
    flex-direction: column;
  }
`

const InvisibleButton = styled(Box)`
  cursor: pointer;
`

const StartDate = styled(Box)`
  border-radius: ${BorderRadixes.regular};
`

const TabsContainer = styled(Box)`
  width: 100%;
  background: #f5f7fa;
  border-radius: 16px;
  padding: 8px 8px 8px 8px;
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  .MuiTabs-root {
    min-height: 40px;
    background: transparent;
    border-radius: 16px;
    width: 100%;
  }
  .MuiTab-root {
    min-height: 40px;
    text-transform: none;
    font-size: 1rem;
    font-weight: 400;
    color: #374151 !important;
    background: transparent;
    border-radius: 16px;
    margin-right: 8px;
    transition:
      background 0.2s,
      font-weight 0.2s,
      color 0.2s,
      border 0.2s;
    border: none;
    padding: 0 32px;
    &.Mui-selected {
      background: #fff;
      color: #111928 !important;
      font-weight: 700;
      border: 2px solid #2563eb;
      border-radius: 16px;
      z-index: 2;
    }
    &:not(.Mui-selected) {
      font-weight: 400;
      color: #374151 !important;
      border: none;
      background: transparent;
    }
    &:first-of-type {
      min-width: 120px;
    }
  }
  .MuiTabs-indicator {
    display: none;
  }
`

const TabContent = styled(Box)``

const MonthHeader = styled.h4`
  font-size: 14px;
  color: rgb(176, 176, 176);
  text-transform: uppercase;
`

const ShowPastLink = styled.button`
  background: none;
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 13px;
  color: #9ca3af;
  text-align: left;
  width: 100%;
  &:hover {
    color: #d1d5db;
    text-decoration: underline;
  }
`

const RoleRestrictionInfoBox = styled(Box)`
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  & span {
    color: #9a3412 !important;
  }
`

const UserTypeBox = styled(Box)<{ hasError?: boolean }>`
  border: 2px solid ${(props) => (props.hasError ? '#dc2626' : '#aaa')};
  border-radius: 12px;
  width: 100%;
  background: ${(props) => (props.hasError ? '#fff1f2' : 'transparent')};
`

const StyledRadioGroup = muiStyled(RadioGroup)`
  gap: 6px;
`

const StyledRadio = muiStyled(Radio)`
  padding: 4px;
`

const StyledFormControlLabel = muiStyled(FormControlLabel)`
  border-radius: 12px;
  margin: 0;
  padding: 8px 16px;
  color: black;
  
  &.Mui-checked {
    background-color: #0E0349;
    
    .MuiTypography-root {
      color: black;
    }
  }

  .MuiTypography-root {
    color: black;
  }
`

const PartnerBox = styled(Box)`
  padding: 8px 0px;
  & label {
    cursor: pointer;
  }
`

const PartnerBoxCheckbox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const PartnerInfoNotice = styled(Box)`
  padding: 8px 0px 4px 0px;
  color: #666;
  font-style: italic;
  font-size: 13px;

  & span {
    color: #666 !important;
  }
`

const PartnerBoxInput = styled(Box)`
  flex-grow: 1;
  width: 100%;
`

const ChildBox = styled(Box)`
  padding: 8px 0px;
`

const ChildFieldsRow = styled(Box)`
  flex-wrap: wrap;
`

const ChildErrorText = styled.span`
  display: block;
  font-size: 0.75rem;
  color: #d32f2f;
  margin-top: 2px;
`

const ChildInput = styled.input<{ hasError?: boolean }>`
  flex: 1;
  min-width: 120px;
  padding: 8px 10px;
  border: 1px solid ${(props) => (props.hasError ? '#dc2626' : '#ccc')};
  border-radius: 6px;
  font-size: 14px;
  color: #111;
  background: #fff;
  outline: none;
  &:focus {
    border-color: ${(props) => (props.hasError ? '#dc2626' : '#7c3aed')};
  }
  &:disabled {
    background: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`

const PackagePill = styled(Box)<{ isSelected?: boolean }>`
  cursor: pointer;
  border: ${(props) => (props.isSelected ? '2px solid #7c3aed' : '1px solid #ddd')};
  border-radius: 8px;
  padding: 16px 20px;
  background: #fff;
  transition: all 0.2s ease;
  position: relative;
  min-width: 150px;
  flex: 1;

  &:hover {
    background: #f9f9f9;
    border-color: #7c3aed;
  }
`

const PackagePillsContainer = styled(Box)`
  flex-wrap: wrap;
  width: 100%;
  gap: 12px;
`

const SectionHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  background: transparent;
  color: #000;
`

const SectionContainer = styled(Box)`
  margin-bottom: 32px;
`

const DayHeader = styled(Box)<{ dayColor?: string }>`
  background: ${(props) => props.dayColor || '#7c3aed'};
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  text-transform: uppercase;
`

const ActivityItem = styled(Box)`
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`

const ActivityRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
`

const CheckboxColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  width: 30px;
  padding-top: 2px;
`

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const ActivityRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
`

const ActivityInfo = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`

const ActivityPrice = styled.div`
  color: #666;
  font-weight: 500;
  min-width: 80px;
  text-align: right;
  flex-shrink: 0;
  white-space: nowrap;
`

const ActivityTime = styled.div`
  color: #999;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
`

export default memo(CourseRegistrationStepTwo)
